import React, { createContext, useContext, useReducer, useEffect, useCallback } from 'react';
import api from '../services/api.js';

const HRMSContext = createContext();

const initialState = {
  workers: [],
  attendance: [],
  leaves: [],
  leaveTypes: [],
  holidays: [],
  shifts: [],
  payroll: []
};

function hrmsReducer(state, action) {
  switch (action.type) {
    case 'SET_WORKERS':
      return { ...state, workers: action.payload };
    case 'SET_ATTENDANCE':
      return { ...state, attendance: action.payload };
    case 'SET_LEAVES':
      return { ...state, leaves: action.payload };
    case 'SET_LEAVE_TYPES':
      return { ...state, leaveTypes: action.payload };
    case 'SET_HOLIDAYS':
      return { ...state, holidays: action.payload };
    case 'SET_SHIFTS':
      return { ...state, shifts: action.payload };
    case 'SET_PAYROLL':
      return { ...state, payroll: action.payload };
    case 'ADD_LEAVE':
      return { ...state, leaves: [...state.leaves, action.payload] };
    case 'UPDATE_LEAVE_STATUS':
      return {
        ...state,
        leaves: state.leaves.map(leave =>
          leave.id === action.payload.id
            ? { ...leave, status: action.payload.status }
            : leave
        )
      };
    default:
      return state;
  }
}

export function HRMSProvider({ children }) {
  const [state, dispatch] = useReducer(hrmsReducer, initialState);

  // Load all data (only for initial app load)
  const loadData = async () => {
    try {
      const [employees, attendance, leaves, leaveTypes, holidays, shifts, payroll] = await Promise.all([
        api.workers.getAll().catch(() => []),
        api.attendance.getAll().catch(() => []),
        api.leaves.getAll().catch(() => []),
        api.leaveTypes.getAll().catch(() => []),
        api.holidays.getAll().catch(() => []),
        api.shifts.getAll().catch(() => []),
        api.payroll.getAll().catch(() => [])
      ]);
      
      dispatch({ type: 'SET_WORKERS', payload: Array.isArray(employees) ? employees : [] });
      dispatch({ type: 'SET_ATTENDANCE', payload: Array.isArray(attendance) ? attendance : [] });
      dispatch({ type: 'SET_LEAVES', payload: Array.isArray(leaves) ? leaves : [] });
      dispatch({ type: 'SET_LEAVE_TYPES', payload: Array.isArray(leaveTypes) ? leaveTypes : [] });
      dispatch({ type: 'SET_HOLIDAYS', payload: Array.isArray(holidays) ? holidays : [] });
      dispatch({ type: 'SET_SHIFTS', payload: Array.isArray(shifts) ? shifts : [] });
      dispatch({ type: 'SET_PAYROLL', payload: Array.isArray(payroll) ? payroll : [] });
    } catch (error) {
      console.error('Error loading data:', error);
      dispatch({ type: 'SET_WORKERS', payload: [] });
      dispatch({ type: 'SET_ATTENDANCE', payload: [] });
      dispatch({ type: 'SET_LEAVES', payload: [] });
      dispatch({ type: 'SET_LEAVE_TYPES', payload: [] });
      dispatch({ type: 'SET_HOLIDAYS', payload: [] });
      dispatch({ type: 'SET_SHIFTS', payload: [] });
      dispatch({ type: 'SET_PAYROLL', payload: [] });
    }
  };

  // Specific load functions for each module
  const loadWorkers = useCallback(async () => {
    try {
      const workers = await api.workers.getAll();
      dispatch({ type: 'SET_WORKERS', payload: Array.isArray(workers) ? workers : [] });
    } catch (error) {
      console.error('Error loading workers:', error);
      dispatch({ type: 'SET_WORKERS', payload: [] });
    }
  }, []);

  const loadAttendance = useCallback(async () => {
    try {
      const [workers, attendance] = await Promise.all([
        api.workers.getAll().catch(() => []),
        api.attendance.getAll().catch(() => [])
      ]);
      dispatch({ type: 'SET_WORKERS', payload: Array.isArray(workers) ? workers : [] });
      dispatch({ type: 'SET_ATTENDANCE', payload: Array.isArray(attendance) ? attendance : [] });
    } catch (error) {
      console.error('Error loading attendance:', error);
      dispatch({ type: 'SET_ATTENDANCE', payload: [] });
    }
  }, []);

  const loadLeaves = useCallback(async () => {
    try {
      const [workers, leaves, leaveTypes] = await Promise.all([
        api.workers.getAll().catch(() => []),
        api.leaves.getAll().catch(() => []),
        api.leaveTypes.getAll().catch(() => [])
      ]);
      dispatch({ type: 'SET_WORKERS', payload: Array.isArray(workers) ? workers : [] });
      dispatch({ type: 'SET_LEAVES', payload: Array.isArray(leaves) ? leaves : [] });
      dispatch({ type: 'SET_LEAVE_TYPES', payload: Array.isArray(leaveTypes) ? leaveTypes : [] });
    } catch (error) {
      console.error('Error loading leaves:', error);
      dispatch({ type: 'SET_LEAVES', payload: [] });
      dispatch({ type: 'SET_LEAVE_TYPES', payload: [] });
    }
  }, []);

  const loadHolidays = useCallback(async () => {
    try {
      const holidays = await api.holidays.getAll();
      dispatch({ type: 'SET_HOLIDAYS', payload: Array.isArray(holidays) ? holidays : [] });
    } catch (error) {
      console.error('Error loading holidays:', error);
      dispatch({ type: 'SET_HOLIDAYS', payload: [] });
    }
  }, []);

  const loadShifts = useCallback(async () => {
    try {
      const shifts = await api.shifts.getAll();
      dispatch({ type: 'SET_SHIFTS', payload: Array.isArray(shifts) ? shifts : [] });
    } catch (error) {
      console.error('Error loading shifts:', error);
      dispatch({ type: 'SET_SHIFTS', payload: [] });
    }
  }, []);

  const loadLeaveTypes = useCallback(async () => {
    try {
      const leaveTypes = await api.leaveTypes.getAll();
      dispatch({ type: 'SET_LEAVE_TYPES', payload: Array.isArray(leaveTypes) ? leaveTypes : [] });
    } catch (error) {
      console.error('Error loading leave types:', error);
      dispatch({ type: 'SET_LEAVE_TYPES', payload: [] });
    }
  }, []);

  const loadPayroll = useCallback(async () => {
    try {
      const [workers, payroll] = await Promise.all([
        api.workers.getAll().catch(() => []),
        api.payroll.getAll().catch(() => [])
      ]);
      dispatch({ type: 'SET_WORKERS', payload: Array.isArray(workers) ? workers : [] });
      dispatch({ type: 'SET_PAYROLL', payload: Array.isArray(payroll) ? payroll : [] });
    } catch (error) {
      console.error('Error loading payroll:', error);
      dispatch({ type: 'SET_PAYROLL', payload: [] });
    }
  }, []);

  useEffect(() => {
    // Load data on startup
    loadData();
  }, []);

  return (
    <HRMSContext.Provider value={{ 
      state, 
      dispatch, 
      loadData, 
      loadWorkers, 
      loadAttendance, 
      loadLeaves, 
      loadLeaveTypes,
      loadHolidays, 
      loadShifts, 
      loadPayroll 
    }}>
      {children}
    </HRMSContext.Provider>
  );
}

export function useHRMS() {
  const context = useContext(HRMSContext);
  if (!context) {
    throw new Error('useHRMS must be used within HRMSProvider');
  }
  return context;
}